








// components
import { Component, Prop, Vue } from 'vue-property-decorator';
import createSelect from '@/components/common/CreateSelect.vue';

// gql
import { partnerService } from '@/services/PartnerService.ts';
import { PartnerFields } from '@/types/graphql/schemaTypes';
import fetchPartnerOptionsQuery from '@/graphql/queries/partners/FetchPartnerOptions.gql';

// enum for partner keys, even if it's a type it's deeply
// linked to this very component
export enum PartnersListDisplayKeysEnum {
  code = 'code',
  name = 'name'
}

@Component({
  apollo: {
    partnerOptions: {
      query: fetchPartnerOptionsQuery,
      update(data) {
        return data?.partners;
      },
      fetchPolicy: 'cache-and-network',
      error() {
        this.$store.dispatch('Alert/addAlert', {
          type: 'error',
          message: this.$t('error.fetch'),
          time: 2000
        });
      },
      skip() {
        return this.partners;
      }
    }
  },
  components: {
    createSelect
  }
})
/**
 * partners   options list for performances. If not present gql request is made
 * value      selected partner's id
 * partnerKey wether the label is code or name
 */
export default class PartnerCreateSelector extends Vue {
  @Prop({ default: null, type: [Array, null as any] }) partners!: PartnerFields[] | null;
  @Prop({ default: null, type: [String, null as any] }) value!: string | null;
  @Prop({ default: false, type: Boolean }) disabled!: boolean;
  @Prop({ default: PartnersListDisplayKeysEnum.code, type: String })
    partnerKey!: PartnersListDisplayKeysEnum;

  // Properties for the create selector
  private partnerOptions: PartnerFields[] | null = [];
  /**
   * @args value    Description to be created
   * @args fail     On fail callback
   * @args success  On success callback
   */
  private createFunc = async(value, success, fail) => {
    if (!value) {
      fail();
    } else {
      try {
        await this.createPartner(value);
        success();
      } catch (error) {
        fail(error);
      }
    }
  };

  get content(): string {
    return this.value;
  }

  set content(value: string) {
    this.$emit('input', value);
    this.$emit('change', value);
  }

  // Representing the provided Prop data or a fresh GQL query
  get options(): PartnerFields[] | null {
    return this.partners ? this.partners : this.partnerOptions;
  }

  private formatOptions() {
    // format the options according to the given key
    switch (this.partnerKey) {
      case PartnersListDisplayKeysEnum.name:
        return this.options.map(item => {
          return { value: item.name, label: item.code, id: item.id };
        });
      case PartnersListDisplayKeysEnum.code:
      default:
        return this.options.map(item => {
          return { value: item.code, label: item.name, id: item.id };
        });
    }
  }

  /**
 * Create Partner: Creates a partner and returns the ID of it.
 *
 * @param   {[string]}  name  The name for a new partner organization
 *
 * @return  {[id]}        The ID of a newly created partner organization
 */
  private async createPartner(name) {
    const response = await partnerService.upsertPartner({ name });

    if (response) {
      this.$apollo.queries.partnerOptions.refetch();
      this.$emit('partnerCreation');
      this.$emit('input', response.data?.upsertPartner?.id);
      this.$emit('change', response.data?.upsertPartner?.id);
    }
  }
}
